import React, { FC, useMemo } from 'react';
import { connect } from 'react-redux';

import cn from 'classnames';
import css from 'components/Admin/AttendanceDetails.module.scss';
import Dialog from 'components/Dialog';
import scss from 'components/shared.module.scss';
import { Button, LoaderSpinner } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { DATE_FORMAT } from 'globals/constants';
import { RootState } from 'redux/reducers';
import { useStudentAttendanceDetailsAPI } from 'services/course-api';

interface StudentAttendanceDetailsProps {
  isOpen: boolean;
  student: {
    userId: string;
    name: string;
    emailId: string;
  } | null;
  getAccess: SessionData | null;
  onClose(): any;
}

const StudentAttendanceDetails: FC<StudentAttendanceDetailsProps> = ({
  isOpen,
  student,
  onClose,
  getAccess,
}) => {
  const { data: payload = null } = useStudentAttendanceDetailsAPI(getAccess, student?.userId);

  const attendances: ClassAttendance[] = useMemo(() => {
    if (!payload) return [];
    if (payload.type === 'individual') return payload.classData;
    return payload.studentsData.flatMap((s) => s.classData);
  }, [payload]);

  const totalAttendanceRecorded = useMemo(
    () => attendances.filter((s) => s.attendanceRecorded).length,
    [attendances]
  );

  const totalClassesAttendedPercentage = useMemo(
    () =>
      attendances.length
        ? (
            (attendances.filter(
              (s) => s.attendanceRecorded && (s.status === 'present' || s.status === 'late')
            ).length /
              totalAttendanceRecorded) *
            100
          ).toFixed(2)
        : 0,
    [attendances, totalAttendanceRecorded]
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className={css['class-attendance-details']}>
          <div className={css.number}>{student?.name}</div>
          <div className={css.title}>{student?.emailId}</div>
          <div className={css['total-percentage-attendance']}>
            {totalClassesAttendedPercentage}% classes attended
          </div>
        </div>
      }
      footer={
        <Button variant='text' color='success' size='small' onClick={onClose} preventHoverStyles>
          DONE
        </Button>
      }
    >
      {payload === null ? (
        <div className={scss['loader-wrapper']}>
          <LoaderSpinner />
        </div>
      ) : payload.type === 'individual' ? (
        <AttendanceTable key={payload.courseCode} payload={payload} />
      ) : (
        payload.studentsData.map((p) => <AttendanceTable key={p.courseCode} payload={p} />)
      )}
    </Dialog>
  );
};

const getAttendanceData = (s: ClassAttendance) => {
  const recorded = {
    label: s.attendanceRecorded ? 'Yes' : 'No',
    style: s.attendanceRecorded ? 'table__cell-value--green' : 'table__cell-value--light-grey',
  };
  switch (s.status) {
    case 'present':
      return {
        recorded,
        status: {
          style: 'table__cell-value--green',
          label: 'Present',
        },
      };
    case 'excused':
      return {
        recorded,
        status: {
          style: 'table__cell-value--orange',
          label: 'Excused',
        },
      };
    case 'late':
      return {
        recorded,
        status: {
          style: 'table__cell-value--orange',
          label: 'Late',
        },
      };
    case 'absent':
      return {
        recorded,
        status: {
          style: 'table__cell-value--red',
          label: 'Absent',
        },
      };
    case 'checkedIn':
      return {
        recorded,
        status: {
          style: 'table__cell-value--dark-grey',
          label: 'Checked In',
        },
      };
    default:
      return {
        recorded,
        status: {
          style: 'table__cell-value--light-grey',
          label: '--',
        },
      };
  }
};

interface AttendanceTableProps {
  payload: CourseAttendance;
}

const AttendanceTable: FC<AttendanceTableProps> = ({ payload: p }) => {
  const attendances = p.classData;
  const presentStudents = useMemo(
    () => attendances.filter((s) => s.attendanceRecorded && s.status === 'present'),
    [attendances]
  );
  const absentStudents = useMemo(
    () => attendances.filter((s) => s.attendanceRecorded && s.status === 'absent'),
    [attendances]
  );
  const lateStudents = useMemo(
    () => attendances.filter((s) => s.attendanceRecorded && s.status === 'late'),
    [attendances]
  );
  const excusedStudents = useMemo(
    () => attendances.filter((s) => s.attendanceRecorded && s.status === 'excused'),
    [attendances]
  );

  return (
    <div className={css['class-attendance-details-body']}>
      <div className={css.header}>
        <div className={css['header-row']}>
          <div>
            {p.courseCode} - {p.title}
          </div>
          <div>
            {presentStudents.length} present, {lateStudents.length} late, {excusedStudents.length} excused,{' '}
            {absentStudents.length} absent
          </div>
        </div>
        <div className={css['header-row']}>
          <div>{p.admin.name}</div>
          {!attendances.length ? (
            <div>No attendance recorded</div>
          ) : (
            <div>
              Attendance recorded from {attendances.length} session
              {attendances.length === 1 ? '' : 's'}
            </div>
          )}
        </div>
      </div>
      <table className='table'>
        <thead>
          <tr>
            <th className='table__cell table__cell--center'>Week No.</th>
            <th className='table__cell table__cell--center'>Session Date</th>
            <th className='table__cell table__cell--center'>Attendance Recorded</th>
            <th className='table__cell table__cell--center'>Status</th>
          </tr>
        </thead>
        <tbody>
          {p.classData.map((s, i) => {
            const data = getAttendanceData(s);
            return (
              <tr key={i}>
                <td className='table__cell table__cell--center'>{s.weekNum}</td>
                <td className='table__cell table__cell--center'>{dt.format(s.startTime, DATE_FORMAT)}</td>
                <td className={cn('table__cell table__cell--center', data.recorded.style)}>
                  {data.recorded.label}
                </td>
                <td className={cn('table__cell table__cell--center', data.status.style)}>
                  {data.status.label}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    getAccess: state.getAccess,
  };
};

export default connect(mapStateToProps)(StudentAttendanceDetails);
